import React, { useState, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  Store as StoreIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import byteLogoFull from '../../assets/byte-logo-full.svg';

export interface MobileStore {
  storeNumber: string;
  name: string;
  isActive?: boolean;
}

// Number of stores to show initially before "Show more"
const INITIAL_VISIBLE_COUNT = 20;
const LOAD_MORE_COUNT = 20;

// Memoized store list item to prevent unnecessary re-renders
interface StoreListItemProps {
  store: MobileStore;
  isSelected: boolean;
  onSelect: (storeNumber: string) => void;
  isLast: boolean;
}

const StoreListItem = memo<StoreListItemProps>(({ store, isSelected, onSelect, isLast }) => {
  const handleClick = useCallback(() => {
    onSelect(store.storeNumber);
  }, [onSelect, store.storeNumber]);

  return (
    <ListItem disablePadding divider={!isLast}>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2,
          bgcolor: isSelected ? 'rgba(102, 44, 139, 0.06)' : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: isSelected ? 'rgba(102, 44, 139, 0.1)' : 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background-color 0.2s ease',
            }}
          >
            <StoreIcon
              sx={{
                color: isSelected ? 'primary.main' : 'grey.500',
                fontSize: 22,
                transition: 'color 0.2s ease',
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={isSelected ? 600 : 500}
              color={isSelected ? 'primary.main' : 'text.primary'}
            >
              #{store.storeNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {store.name}
            </Typography>
          </Box>
          {store.isActive !== undefined && (
            <Chip
              size="small"
              label={store.isActive ? 'Active' : 'Inactive'}
              color={store.isActive ? 'success' : 'default'}
              sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
          {isSelected && <CheckIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />}
        </Box>
      </ListItemButton>
    </ListItem>
  );
});

StoreListItem.displayName = 'StoreListItem';

interface MobileStorePickerProps {
  stores: MobileStore[];
  selectedStore: string | null;
  onStoreChange: (storeNumber: string) => void;
  loading?: boolean;
}

export const MobileStorePicker: React.FC<MobileStorePickerProps> = ({
  stores,
  selectedStore,
  onStoreChange,
  loading = false,
}) => {
  const [expanded, setExpanded] = useState(!selectedStore);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  // Memoize selected store lookup
  const selectedStoreData = useMemo(
    () => stores.find((s) => s.storeNumber === selectedStore),
    [stores, selectedStore]
  );

  // Memoize filtered stores based on search
  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) return stores;
    const query = searchQuery.toLowerCase().trim();
    return stores.filter(
      (store) =>
        store.storeNumber.toLowerCase().includes(query) || store.name.toLowerCase().includes(query)
    );
  }, [stores, searchQuery]);

  // Memoize visible stores (paginated)
  const visibleStores = useMemo(
    () => filteredStores.slice(0, visibleCount),
    [filteredStores, visibleCount]
  );

  const hasMoreStores = filteredStores.length > visibleCount;
  const remainingCount = filteredStores.length - visibleCount;

  // Memoized callbacks
  const handleStoreSelect = useCallback(
    (storeNumber: string) => {
      onStoreChange(storeNumber);
      setExpanded(false);
      setSearchQuery('');
      setVisibleCount(INITIAL_VISIBLE_COUNT);
    },
    [onStoreChange]
  );

  const handleToggleExpand = useCallback(() => {
    if (!loading) {
      setExpanded((prev) => !prev);
      if (expanded) {
        // Reset search and pagination when collapsing
        setSearchQuery('');
        setVisibleCount(INITIAL_VISIBLE_COUNT);
      }
    }
  }, [loading, expanded]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setVisibleCount(INITIAL_VISIBLE_COUNT); // Reset pagination on search
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + LOAD_MORE_COUNT);
  }, []);

  // If only one store, show compact branded header
  if (stores.length === 1) {
    const store = stores[0];
    return (
      <>
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100 }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              pt: 'calc(env(safe-area-inset-top, 0px) + 12px)',
              background: 'linear-gradient(135deg, #662C8B 0%, #8B3FA8 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={byteLogoFull}
              alt="Byte"
              sx={{ height: 24, filter: 'brightness(0) invert(1)' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="white">
                #{store.storeNumber}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }} noWrap>
                {store.name}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Spacer for fixed header */}
        <Box sx={{ height: 'calc(env(safe-area-inset-top, 0px) + 60px)' }} />
      </>
    );
  }

  // Multiple stores - unified design with collapse
  const isExpanded = expanded || !selectedStore;
  const showSearch = stores.length > 10; // Only show search if more than 10 stores

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          bgcolor: 'background.paper',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {/* Branded Header - always visible, transitions between states */}
        <Box
          onClick={selectedStore && !isExpanded ? handleToggleExpand : undefined}
          sx={{
            background: 'linear-gradient(135deg, #662C8B 0%, #8B3FA8 100%)',
            color: 'white',
            cursor: selectedStore && !isExpanded ? 'pointer' : 'default',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
          }}
        >
          {/* Logo and Title Row */}
          <Box
            sx={{
              px: 2.5,
              pt: isExpanded
                ? 'calc(env(safe-area-inset-top, 0px) + 24px)'
                : 'calc(env(safe-area-inset-top, 0px) + 12px)',
              pb: isExpanded ? 2 : 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
              <Box
                component="img"
                src={byteLogoFull}
                alt="Byte"
                sx={{
                  height: isExpanded ? 28 : 22,
                  filter: 'brightness(0) invert(1)',
                  transition: 'height 0.3s ease',
                  flexShrink: 0,
                }}
              />
              {!isExpanded && selectedStoreData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.5, flexShrink: 0 }}>
                    |
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                      #{selectedStoreData.storeNumber}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.85, lineHeight: 1, display: 'block' }}
                      noWrap
                    >
                      {selectedStoreData.name}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            {selectedStore && (
              <IconButton
                size="small"
                sx={{ color: 'white', opacity: 0.8, flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand();
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>

          {/* Expanded content - title and description */}
          <Collapse in={isExpanded} timeout={300}>
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                Store Control
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.4 }}>
                Manage store hours, channels, and item availability
              </Typography>
            </Box>
          </Collapse>
        </Box>

        {/* Store Selection Section */}
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ bgcolor: '#f8f9fa', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
              letterSpacing={1}
            >
              {selectedStore ? 'Change store' : 'Select a store to get started'}
              {filteredStores.length !== stores.length && ` (${filteredStores.length} results)`}
            </Typography>
          </Box>
        </Collapse>

        {/* Search Field - only show for many stores */}
        <Collapse in={isExpanded && showSearch} timeout={300}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.paper' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by store number or name..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8f9fa',
                },
              }}
            />
          </Box>
        </Collapse>

        {/* Store List */}
        <Collapse in={isExpanded} timeout={300}>
          <Box
            sx={{
              maxHeight: '50vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {visibleStores.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No stores found matching "{searchQuery}"
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {visibleStores.map((store, index) => (
                  <StoreListItem
                    key={store.storeNumber}
                    store={store}
                    isSelected={selectedStore === store.storeNumber}
                    onSelect={handleStoreSelect}
                    isLast={index === visibleStores.length - 1 && !hasMoreStores}
                  />
                ))}
              </List>
            )}

            {/* Load More Button */}
            {hasMoreStores && (
              <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="text"
                  onClick={handleLoadMore}
                  sx={{ textTransform: 'none', color: 'primary.main' }}
                >
                  Show {Math.min(remainingCount, LOAD_MORE_COUNT)} more stores
                  {remainingCount > LOAD_MORE_COUNT && ` (${remainingCount} remaining)`}
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Spacer for fixed header - keeps content from going behind header */}
      <Box
        sx={{
          height: isExpanded ? '100dvh' : 'calc(env(safe-area-inset-top, 0px) + 64px)',
          transition: 'height 0.3s ease',
        }}
      />
    </>
  );
};
